/**
 * Catálogo contable de Tumin: los *roles* de cuenta que usan las partidas
 * automáticas, su naturaleza (deudora o acreedora), el catálogo de cuentas
 * sugerido para una institución de microfinanzas guatemalteca y la política
 * contable configurable (umbrales de cartera vencida y de suspensión del
 * devengo, IVA sobre comisiones).
 *
 * Las partidas nunca usan números de cuenta fijos: usan un rol
 * (`loans_current`) y la configuración de la institución lo traduce al
 * código y nombre de *su* catálogo. El código y el nombre se copian en cada
 * línea al contabilizar, para que un cambio posterior del catálogo no
 * altere la historia.
 */
import { keyedEnum } from '../catalogs/enum.js';
/** Roles de cuenta que alimentan las partidas automáticas. */
export const ACCOUNT_ROLES = [
    'cash_on_hand',
    'bank',
    'loans_current',
    'loans_past_due',
    'loan_loss_provision',
    'interest_receivable',
    'vat_payable',
    'opening_balance',
    'interest_income',
    'late_interest_income',
    'fee_income',
    'late_fee_income',
    'written_off_recoveries',
    'provision_expense',
];
/** Catálogo con clave: `ACCOUNT_ROLE.loans_current`. */
export const ACCOUNT_ROLE = keyedEnum(ACCOUNT_ROLES);
/**
 * Naturaleza de una cuenta: las deudoras (activo, gasto) crecen con cargos
 * (debe) y las acreedoras (pasivo, patrimonio, ingreso y cuentas de
 * valuación del activo) crecen con abonos (haber).
 */
export const ACCOUNT_NATURES = ['debit', 'credit'];
/** Catálogo con clave: `ACCOUNT_NATURE.debit`. */
export const ACCOUNT_NATURE = keyedEnum(ACCOUNT_NATURES);
/** Grupos del catálogo, en el orden en que aparecen en la balanza. */
export const ACCOUNT_GROUPS = ['asset', 'liability', 'equity', 'income', 'expense'];
/** Catálogo con clave: `ACCOUNT_GROUP.asset`. */
export const ACCOUNT_GROUP = keyedEnum(ACCOUNT_GROUPS);
/** Nombre en español de cada grupo del catálogo. */
export const ACCOUNT_GROUP_LABELS = {
    asset: 'Activo',
    liability: 'Pasivo',
    equity: 'Patrimonio',
    income: 'Productos (ingresos)',
    expense: 'Gastos',
};
/** Qué registra cada rol de cuenta. */
export const ACCOUNT_ROLE_DEFINITIONS = {
    cash_on_hand: {
        label: 'Caja',
        description: 'Efectivo recibido en caja y desembolsos en efectivo.',
        group: 'asset',
        nature: 'debit',
    },
    bank: {
        label: 'Bancos',
        description: 'Depósitos, transferencias, cheques y billeteras móviles.',
        group: 'asset',
        nature: 'debit',
    },
    loans_current: {
        label: 'Cartera vigente',
        description: 'Capital de los préstamos con atraso menor o igual al umbral de cartera vencida.',
        group: 'asset',
        nature: 'debit',
    },
    loans_past_due: {
        label: 'Cartera vencida',
        description: 'Capital de los préstamos que superaron el umbral de días de atraso.',
        group: 'asset',
        nature: 'debit',
    },
    loan_loss_provision: {
        label: 'Estimación por incobrabilidad',
        description: 'Cuenta de valuación (resta de la cartera): reserva requerida según la categoría de riesgo.',
        group: 'asset',
        nature: 'credit',
    },
    interest_receivable: {
        label: 'Intereses por cobrar',
        description: 'Intereses devengados y aún no cobrados de préstamos que siguen devengando.',
        group: 'asset',
        nature: 'debit',
    },
    vat_payable: {
        label: 'IVA por pagar',
        description: 'IVA débito fiscal cobrado sobre intereses, moratorios y comisiones.',
        group: 'liability',
        nature: 'credit',
    },
    opening_balance: {
        label: 'Saldos de apertura',
        description: 'Contrapartida transitoria de la carga inicial de la cartera existente; su contador la liquida contra la cuenta que corresponda.',
        group: 'equity',
        nature: 'credit',
    },
    interest_income: {
        label: 'Intereses sobre cartera',
        description: 'Interés corriente devengado o cobrado.',
        group: 'income',
        nature: 'credit',
    },
    late_interest_income: {
        label: 'Intereses moratorios',
        description: 'Interés moratorio, reconocido al cobrarse.',
        group: 'income',
        nature: 'credit',
    },
    fee_income: {
        label: 'Comisiones por otorgamiento',
        description: 'Comisión de desembolso y cargos fijos descontados al desembolsar.',
        group: 'income',
        nature: 'credit',
    },
    late_fee_income: {
        label: 'Gastos de cobranza',
        description: 'Cargo fijo por atraso cobrado al cliente.',
        group: 'income',
        nature: 'credit',
    },
    written_off_recoveries: {
        label: 'Recuperación de cartera castigada',
        description: 'Cobros de préstamos ya castigados.',
        group: 'income',
        nature: 'credit',
    },
    provision_expense: {
        label: 'Gasto por estimación de incobrabilidad',
        description: 'Constitución (y liberación, en negativo) de la estimación por incobrabilidad.',
        group: 'expense',
        nature: 'debit',
    },
};
/**
 * Catálogo de cuentas SUGERIDO para una IMF u ONG de microfinanzas en
 * Guatemala. Sigue la estructura del Manual de Instrucciones Contables de la
 * SIB (1 activo, 2 pasivo, 3 patrimonio, 5 productos, 6 gastos) con una
 * codificación simple de cuatro dígitos más auxiliar. Es un punto de
 * partida: cada institución debe ajustarlo con su contador a su propio
 * catálogo autorizado.
 */
export const DEFAULT_ACCOUNT_MAPPING = {
    cash_on_hand: { code: '1101.01', name: 'Caja general' },
    bank: { code: '1102.01', name: 'Bancos — cuenta monetaria' },
    loans_current: { code: '1201.01', name: 'Cartera de créditos vigente' },
    loans_past_due: { code: '1201.02', name: 'Cartera de créditos vencida' },
    loan_loss_provision: { code: '1209.01', name: 'Estimación por valuación de cartera de créditos (−)' },
    interest_receivable: { code: '1301.01', name: 'Productos financieros por cobrar — intereses' },
    vat_payable: { code: '2103.01', name: 'IVA débito fiscal por pagar' },
    opening_balance: { code: '3901.01', name: 'Saldos de apertura por conciliar' },
    interest_income: { code: '5101.01', name: 'Intereses sobre cartera de créditos' },
    late_interest_income: { code: '5101.02', name: 'Intereses moratorios' },
    fee_income: { code: '5102.01', name: 'Comisiones por otorgamiento de créditos' },
    late_fee_income: { code: '5102.02', name: 'Gastos de cobranza recuperados' },
    written_off_recoveries: { code: '5301.01', name: 'Recuperación de cartera castigada' },
    provision_expense: { code: '6201.01', name: 'Estimación por incobrabilidad de cartera' },
};
/**
 * Días de atraso a partir de los cuales el capital de un préstamo pasa de
 * cartera vigente a cartera vencida (más de 90 días), la práctica del
 * Manual de Instrucciones Contables de la SIB para créditos con pagos
 * periódicos. Configurable por institución.
 */
export const DEFAULT_PAST_DUE_THRESHOLD_DAYS = 90;
/**
 * Días de atraso a partir de los cuales se suspende el devengo de
 * intereses (más de 90 días). Prudencia contable alineada con el
 * Reglamento para la Administración del Riesgo de Crédito (JM-93-2005):
 * un préstamo con más de 90 días de atraso ya está en categoría D o E y
 * sus intereses no cobrados no deben inflar los resultados. Al cruzar el
 * umbral se reversan los intereses devengados no cobrados y, desde ese día,
 * el interés se reconoce solo al cobrarse (base de caja).
 */
export const DEFAULT_NON_ACCRUAL_THRESHOLD_DAYS = 90;
/**
 * Tope de días de los umbrales configurables: un año. Un umbral mayor
 * dejaría devengando intereses de préstamos claramente irrecuperables.
 */
export const MAX_ACCOUNTING_THRESHOLD_DAYS = 365;
/** Copia independiente de un catálogo (para que nadie modifique el sugerido por accidente). */
function copyMapping(mapping) {
    return Object.fromEntries(ACCOUNT_ROLES.map((role) => [role, { ...mapping[role] }]));
}
/** Política contable inicial de una institución nueva. */
export function defaultAccountingSettings() {
    return {
        accounts: copyMapping(DEFAULT_ACCOUNT_MAPPING),
        pastDueThresholdDays: DEFAULT_PAST_DUE_THRESHOLD_DAYS,
        nonAccrualThresholdDays: DEFAULT_NON_ACCRUAL_THRESHOLD_DAYS,
        feesIncludeVat: true,
    };
}
/**
 * Completa una configuración contable guardada con los valores por
 * defecto: una institución que actualiza Tumin recibe el catálogo sugerido
 * para los roles que aún no ha mapeado.
 */
export function withAccountingDefaults(stored) {
    const defaults = defaultAccountingSettings();
    return {
        ...defaults,
        ...stored,
        accounts: { ...defaults.accounts, ...stored?.accounts },
    };
}
//# sourceMappingURL=accounts.js.map